import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography,
  Alert,
  Chip,
  Paper,
  CircularProgress,
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Print } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../api';
import type { Printer } from '../printers';
import type { Product } from '../products';
import { getLanguageDisplayName, getAvailableLanguages } from '../product-details/utils';

interface TranslationSummary {
  head_id: string;
  language: string;
  latest_version: number;
  updated_at: string;
}

interface PrintHistoryItem {
  id: string;
  timestamp: Date;
  productName: string;
  ean: string;
  language: string;
  success: boolean;
  error?: string;
}

interface ContinuousPrintingProps {
  printers: Printer[];
  onShowSnackbar: (message: string, severity: 'success' | 'error') => void;
}

type ProcessingStep =
  | { status: 'idle' }
  | { status: 'searching'; ean: string }
  | { status: 'found'; product: Product; ean: string }
  | { status: 'checking-translation'; product: Product; ean: string }
  | { status: 'printing'; product: Product; ean: string }
  | { status: 'success'; product: Product; ean: string }
  | { status: 'error'; ean: string; message: string };

export const ContinuousPrinting: React.FC<ContinuousPrintingProps> = ({ printers, onShowSnackbar }) => {
  const { t } = useTranslation();
  const api = useApi();
  const eanInputRef = useRef<HTMLInputElement>(null);

  const [selectedPrinterId, setSelectedPrinterId] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [eanInput, setEanInput] = useState('');
  const [processingStep, setProcessingStep] = useState<ProcessingStep>({ status: 'idle' });
  const [printHistory, setPrintHistory] = useState<PrintHistoryItem[]>([]);

  const availableLanguages = getAvailableLanguages();
  const submitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const printerControlRef = useRef<HTMLDivElement>(null);
  const languageControlRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (printers.length > 0 && !selectedPrinterId) {
      setSelectedPrinterId(printers[0].id);
    }
  }, [printers, selectedPrinterId]);

  const addToHistory = useCallback((item: Omit<PrintHistoryItem, 'id' | 'timestamp'>) => {
    const historyItem: PrintHistoryItem = {
      ...item,
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
    };
    setPrintHistory((prev) => [historyItem, ...prev.slice(0, 19)]);
  }, []);

  const isReady = Boolean(selectedPrinterId && selectedLanguage);
  const isProcessing =
    processingStep.status === 'searching' ||
    processingStep.status === 'checking-translation' ||
    processingStep.status === 'printing' ||
    processingStep.status === 'found';

  const focusScanInput = useCallback(() => {
    requestAnimationFrame(() => {
      eanInputRef.current?.focus();
    });
  }, []);

  useEffect(() => {
    if (isReady && !isProcessing) {
      focusScanInput();
    }
  }, [isReady, isProcessing, processingStep.status, focusScanInput]);

  const handlePrinterChange = (event: SelectChangeEvent<string>) => {
    setSelectedPrinterId(event.target.value);
  };

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    setSelectedLanguage(event.target.value);
  };

  const processEan = useCallback(
    async (eanCode: string) => {
      const trimmedEan = eanCode.trim();
      if (!trimmedEan) {
        return;
      }

      if (!selectedPrinterId) {
        onShowSnackbar(t('printing.continuous.printerRequired'), 'error');
        return;
      }

      if (!selectedLanguage) {
        onShowSnackbar(t('printing.continuous.languageRequired'), 'error');
        return;
      }

      try {
        setProcessingStep({ status: 'searching', ean: trimmedEan });
        const product: Product = await api.get(`/resolver?ean=${encodeURIComponent(trimmedEan)}`);

        setProcessingStep({ status: 'found', product, ean: trimmedEan });
        await new Promise((resolve) => setTimeout(resolve, 180));

        setProcessingStep({ status: 'checking-translation', product, ean: trimmedEan });

        const translations: TranslationSummary[] = await api.get(`/products/${product.id}/translations?includeText=false`);
        const hasTranslation = translations.some((item) => item.language === selectedLanguage);

        if (!hasTranslation) {
          setProcessingStep({
            status: 'error',
            ean: trimmedEan,
            message: t('printing.continuous.noTranslation'),
          });

          addToHistory({
            productName: product.name,
            ean: trimmedEan,
            language: selectedLanguage,
            success: false,
            error: t('printing.continuous.noTranslation'),
          });

          onShowSnackbar(
            t('printing.continuous.printFailedNoTranslation', {
              product: product.name,
              language: selectedLanguage.toUpperCase(),
            }),
            'error'
          );
          return;
        }

        setProcessingStep({ status: 'printing', product, ean: trimmedEan });

        const payload = {
          product_id: product.id,
          language: selectedLanguage,
          copies: 1,
          correlation_id: `continuous-${Date.now()}`,
        };

        await api.post(`/printers/${selectedPrinterId}/jobs/label`, payload);

        setProcessingStep({ status: 'success', product, ean: trimmedEan });

        addToHistory({
          productName: product.name,
          ean: trimmedEan,
          language: selectedLanguage,
          success: true,
        });

        onShowSnackbar(t('printing.continuous.printSuccess', { product: product.name }), 'success');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t('printing.continuous.error');

        setProcessingStep({
          status: 'error',
          ean: trimmedEan,
          message: errorMessage,
        });

        addToHistory({
          productName: t('printing.continuous.unknownProduct'),
          ean: trimmedEan,
          language: selectedLanguage,
          success: false,
          error: errorMessage,
        });

        onShowSnackbar(t('printing.continuous.printFailed', { ean: trimmedEan }), 'error');
      }
    },
    [addToHistory, api, onShowSnackbar, selectedLanguage, selectedPrinterId, t]
  );

  const handleEanInputChange = (value: string) => {
    if (processingStep.status !== 'idle' && processingStep.status !== 'success' && processingStep.status !== 'error') {
      return;
    }

    setEanInput(value);

    if (submitTimerRef.current) {
      clearTimeout(submitTimerRef.current);
    }

    if (value.trim().length > 0) {
      submitTimerRef.current = setTimeout(() => {
        const nextEan = value;
        setEanInput('');
        processEan(nextEan);
      }, 700);
    }
  };

  useEffect(() => {
    return () => {
      if (submitTimerRef.current) {
        clearTimeout(submitTimerRef.current);
      }
    };
  }, []);

  const getStatusDisplay = () => {
    switch (processingStep.status) {
      case 'idle':
        return null;
      case 'searching':
        return {
          icon: <CircularProgress size={20} />,
          text: t('printing.continuous.status.searching', { ean: processingStep.ean }),
          color: 'info.main' as const,
        };
      case 'found':
        return {
          icon: <CheckCircle />,
          text: t('printing.continuous.status.found', { product: processingStep.product.name }),
          color: 'success.main' as const,
        };
      case 'checking-translation':
        return {
          icon: <CircularProgress size={20} />,
          text: t('printing.continuous.status.checkingTranslation'),
          color: 'info.main' as const,
        };
      case 'printing':
        return {
          icon: <Print />,
          text: t('printing.continuous.status.printing', { product: processingStep.product.name }),
          color: 'primary.main' as const,
        };
      case 'success':
        return {
          icon: <CheckCircle />,
          text: t('printing.continuous.status.success', { product: processingStep.product.name }),
          color: 'success.main' as const,
        };
      case 'error':
        return {
          icon: <ErrorIcon />,
          text: t('printing.continuous.status.error', {
            ean: processingStep.ean,
            message: processingStep.message,
          }),
          color: 'error.main' as const,
        };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <Box>
      <Card elevation={0} sx={{ borderRadius: 1.25, mb: 3 }}>
        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('printing.continuous.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('printing.continuous.instructions')}
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth required ref={printerControlRef}>
                <InputLabel id="continuous-printer-select-label">{t('printing.printerSelectLabel')}</InputLabel>
                <Select
                  labelId="continuous-printer-select-label"
                  value={selectedPrinterId}
                  label={t('printing.printerSelectLabel')}
                  onChange={handlePrinterChange}
                  disabled={isProcessing}
                >
                  {printers.map((printer) => (
                    <MenuItem value={printer.id} key={printer.id}>
                      {printer.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required ref={languageControlRef}>
                <InputLabel id="continuous-language-select-label">{t('printing.continuous.targetLanguage')}</InputLabel>
                <Select
                  labelId="continuous-language-select-label"
                  value={selectedLanguage}
                  label={t('printing.continuous.targetLanguage')}
                  onChange={handleLanguageChange}
                  disabled={isProcessing}
                >
                  {availableLanguages.map((lang) => (
                    <MenuItem value={lang} key={lang}>
                      {getLanguageDisplayName(lang)} ({lang.toUpperCase()})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {!isReady && <Alert severity="info">{t('printing.continuous.selectPrinterAndLanguage')}</Alert>}

            {isReady && (
              <TextField
                inputRef={eanInputRef}
                label={t('printing.eanLabel')}
                placeholder={t('printing.continuous.scanPrompt')}
                value={eanInput}
                onChange={(event) => handleEanInputChange(event.target.value)}
                onBlur={(event) => {
                  const nextFocused = event.relatedTarget as HTMLElement | null;
                  const movingToPrinterControl =
                    !!nextFocused && !!printerControlRef.current && printerControlRef.current.contains(nextFocused);
                  const movingToLanguageControl =
                    !!nextFocused && !!languageControlRef.current && languageControlRef.current.contains(nextFocused);

                  if (!movingToPrinterControl && !movingToLanguageControl && isReady && !isProcessing) {
                    focusScanInput();
                  }
                }}
                disabled={isProcessing}
                autoFocus
                fullWidth
                helperText={t('printing.continuous.scanHelp')}
              />
            )}

            {statusDisplay && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.25,
                  backgroundColor: 'background.paper',
                  border: (theme) => {
                    const colorKey = statusDisplay.color.split('.')[0] as 'info' | 'success' | 'error' | 'primary';
                    return `2px solid ${theme.palette[colorKey].main}`;
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ color: statusDisplay.color }}>{statusDisplay.icon}</Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {statusDisplay.text}
                  </Typography>
                </Stack>
              </Box>
            )}

            {isReady && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.25,
                  backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
                  border: (theme) => `1px solid ${theme.palette.success.main}`,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <CheckCircle color="success" />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('printing.continuous.readyTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('printing.continuous.readyMessage', {
                        printer: printers.find((p) => p.id === selectedPrinterId)?.display_name,
                        language: selectedLanguage.toUpperCase(),
                      })}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {printHistory.length > 0 && (
        <Card elevation={0} sx={{ borderRadius: 1.25 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t('printing.continuous.historyTitle')}
            </Typography>
            <Stack spacing={1} sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {printHistory.map((item) => (
                <Paper
                  key={item.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderLeft: (theme) =>
                      `4px solid ${item.success ? theme.palette.success.main : theme.palette.error.main}`,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                      {item.success ? <Print color="success" fontSize="small" /> : <ErrorIcon color="error" fontSize="small" />}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {item.productName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.ean} • {item.timestamp.toLocaleTimeString()}
                        </Typography>
                        {item.error && (
                          <Typography variant="caption" color="error.main" display="block">
                            {item.error}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Chip label={item.language.toUpperCase()} size="small" />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

